import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import styles from './HospitalScreen.module.css';

interface HospitalizationData {
  id: number;
  hospitalizedAt: string;
  recoveryTime: number;
  hpAtHospitalization: number;
  maxHpAtHospitalization: number;
  mercenaryLevelAtHospitalization: number;
  hospitalizationCount: number;
  isRecovered: boolean;
  AttackerMercenary?: {
    id: number;
    name: string;
  };
  battleReportId?: number;
}

interface MercenaryStats {
  mercenaryId: number;
  mercenaryName: string;
  isCurrentlyHospitalized: boolean;
  hospitalizedUntil: string;
  totalHospitalizations: number;
  hospitalizationsCaused: number;
  hospitalizationCount: number;
}

const HospitalScreen: React.FC = () => {
  const { mercenaryId } = useParams<{ mercenaryId: string }>();
  const [mercenaryStats, setMercenaryStats] = useState<MercenaryStats | null>(null);
  const [hospitalizationHistory, setHospitalizationHistory] = useState<HospitalizationData[]>([]);
  const [timeRemaining, setTimeRemaining] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (mercenaryId) {
      fetchHospitalizationData();
    }
  }, [mercenaryId]);

  useEffect(() => {
    if (mercenaryStats?.isCurrentlyHospitalized && mercenaryStats.hospitalizedUntil) {
      const interval = setInterval(() => {
        updateTimeRemaining();
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [mercenaryStats]);

  const fetchHospitalizationData = async () => {
    try {
      setLoading(true);
      
      // Fetch mercenary stats
      const statsResponse = await axios.get(`/hospitalization/mercenary/${mercenaryId}/stats`);
      setMercenaryStats(statsResponse.data);

      // Fetch hospitalization history
      const historyResponse = await axios.get(`/hospitalization/mercenary/${mercenaryId}/history`);
      setHospitalizationHistory(historyResponse.data);

    } catch (error: any) {
      console.error('Error fetching hospitalization data:', error);
      setError(error.response?.data?.message || 'Failed to load hospitalization data');
    } finally {
      setLoading(false);
    }
  };

  const updateTimeRemaining = () => {
    if (!mercenaryStats?.hospitalizedUntil) return;

    const now = new Date().getTime();
    const recoveryTime = new Date(mercenaryStats.hospitalizedUntil).getTime();
    const difference = recoveryTime - now;

    if (difference > 0) {
      const hours = Math.floor(difference / (1000 * 60 * 60));
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);

      setTimeRemaining(`${hours}h ${minutes}m ${seconds}s`);
    } else {
      setTimeRemaining('Recovery complete - refresh page');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getCurrentHospitalization = () => {
    return hospitalizationHistory.find(h => !h.isRecovered);
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading hospitalization data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>{error}</div>
      </div>
    );
  }

  if (!mercenaryStats) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>Mercenary not found</div>
      </div>
    );
  }

  const currentHospitalization = getCurrentHospitalization();

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>🏥 Hospital</h1>
        <h2>{mercenaryStats.mercenaryName}</h2>
      </div>

      {mercenaryStats.isCurrentlyHospitalized ? (
        <div className={styles.currentHospitalization}>
          <div className={styles.statusCard}>
            <h3>Currently Hospitalized</h3>
            <div className={styles.recoveryInfo}>
              <div className={styles.timeRemaining}>
                <strong>Time Remaining: {timeRemaining}</strong>
              </div>
              <div className={styles.recoveryDate}>
                Recovery Expected: {formatDate(mercenaryStats.hospitalizedUntil)}
              </div>
            </div>

            {currentHospitalization && (
              <div className={styles.injuryDetails}>
                <h4>Injury Details</h4>
                <p>HP at Hospitalization: {currentHospitalization.hpAtHospitalization} / {currentHospitalization.maxHpAtHospitalization}</p>
                <p>Mercenary Level: {currentHospitalization.mercenaryLevelAtHospitalization}</p>
                <p>Hospitalization #{currentHospitalization.hospitalizationCount}</p>
                
                {currentHospitalization.AttackerMercenary && (
                  <p>Injured by: <strong>{currentHospitalization.AttackerMercenary.name}</strong></p>
                )}
                
                {currentHospitalization.battleReportId && (
                  <div className={styles.battleLink}>
                    <Link 
                      to={`/battle/result/${currentHospitalization.battleReportId}`}
                      className={styles.viewBattleButton}
                    >
                      View Battle Report
                    </Link>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className={styles.healthyStatus}>
          <h3>✅ Mercenary is Healthy</h3>
          <p>This mercenary is not currently hospitalized and is ready for battle!</p>
        </div>
      )}

      <div className={styles.statsSection}>
        <h3>Hospitalization Statistics</h3>
        <div className={styles.statsGrid}>
          <div className={styles.statCard}>
            <div className={styles.statNumber}>{mercenaryStats.totalHospitalizations}</div>
            <div className={styles.statLabel}>Times Hospitalized</div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statNumber}>{mercenaryStats.hospitalizationsCaused}</div>
            <div className={styles.statLabel}>Hospitalizations Caused</div>
          </div>
        </div>
      </div>

      {hospitalizationHistory.length > 0 && (
        <div className={styles.historySection}>
          <h3>Hospitalization History</h3>
          <div className={styles.historyList}>
            {hospitalizationHistory.map((hospitalization) => (
              <div key={hospitalization.id} className={styles.historyItem}>
                <div className={styles.historyHeader}>
                  <span className={styles.historyDate}>
                    {formatDate(hospitalization.hospitalizedAt)}
                  </span>
                  <span className={`${styles.historyStatus} ${hospitalization.isRecovered ? styles.recovered : styles.active}`}>
                    {hospitalization.isRecovered ? 'Recovered' : 'Active'}
                  </span>
                </div>
                <div className={styles.historyDetails}>
                  <p>HP: {hospitalization.hpAtHospitalization} / {hospitalization.maxHpAtHospitalization}</p>
                  <p>Recovery Time: {Math.floor(hospitalization.recoveryTime / 60)} hours</p>
                  {hospitalization.AttackerMercenary && (
                    <p>Injured by: {hospitalization.AttackerMercenary.name}</p>
                  )}
                  {hospitalization.battleReportId && (
                    <Link 
                      to={`/battle/result/${hospitalization.battleReportId}`}
                      className={styles.battleReportLink}
                    >
                      View Battle Report
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className={styles.actions}>
        <Link to="/mercenary" className={styles.backButton}>
          Back to Mercenary
        </Link>
      </div>
    </div>
  );
};

export default HospitalScreen;
